import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useExperiences } from 'core/contexts/experience';
import { useExperiences as useExperiencesV2 } from 'core/contexts/experienceV2';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import { isValidPositiveInteger } from '@utriper/core';
import { buildRRule, buildUTCDate, getDateValuesFromUTC, rruleExtractor, UNTIL_TYPE } from './components/ScheduleForm/utils';

export const PRICE_TYPE = {
    FIXED: '1',
    PRICE_GROUP: '2',
};

export const SUBMIT_TYPE = {
    ADD: 1,
    UPDATE: 2,
    DELETE: 3,
};

export const useCalendarPage = () => {
    const { experience } = useExperiencesV2();
    const initialEvent = {
        id: -1,
        // WHEN TAB
        start: null,
        startDuration: null,
        endDuration: null,
        hasEndDate: false,
        end: null,
        timezone: null,
        timePeriod: experience?.timePeriod || false,
        // CAPACITY TAB
        capacity: null,
        unlimited: false,
        privateSlot: false,
        units: [],
        priceType: PRICE_TYPE.FIXED,
        price: 0,
        currency: 'COP',
        priceGroupId: null,
        // REPEAT TAB
        repeat: false,
        frequency: null,
        selectedWeekDays: [],
        intradayRepeat: false,
        byHourDate: null,
        byHour: null,
        byMinuteDate: null,
        byMinute: null,
        untilType: UNTIL_TYPE.INFINITE,
        untilDate: null,
        untilCount: null,
        rrule: null,
    };
    const { t } = useTranslation();
    const translate = (key) => t(`experience_calendar.success_messages.${key}`);
    const { getExperienceSchedule, addExperienceSchedule, updateExperienceSchedule, deleteExperienceSchedule } = useExperiences();

    const [start, setStart] = useState(null);
    const [end, setEnd] = useState(null);
    const [calendarRange, setCalendarRange] = useState({});
    const [clickedEvent, setClickedEvent] = useState({ ...initialEvent });
    const [isEdit, setIsEdit] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [open, setOpen] = useState(false);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [eventToUpdate, setEventToUpdate] = useState(null);
    const [timezone, setTimezone] = useState(null);

    const onSelect = ({ jsEvent: { x, y } = { x: 50, y: 50 }, start = new Date(), end = new Date() }) => {
        setStart(start);
        setEnd(end);
        setPosition({ x, y });
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
        setClickedEvent({ ...initialEvent });
        setIsEdit(false);
        setEventToUpdate(null);
        setOpenUpdate(false);
        setOpenDelete(false);
    };

    const getEvents = async (calendarRange, experienceDetailId, experienceId) => {
        if (calendarRange?.start && calendarRange?.end && experienceDetailId && experienceId) {
            await getExperienceSchedule(calendarRange, experienceDetailId, experienceId);
        }
    };

    const onSubmit = async (values, submitType) => {
        const startUTC = buildUTCDate(values?.start, values?.startDuration, values?.timezone);
        const endDate = values?.hasEndDate ? values?.end : values.start;
        const endUTC = buildUTCDate(endDate, values?.endDuration, values?.timezone);
        const rrule = buildRRule(values);
        let rruleString = null;
        if (rrule?.rruleString) {
            rruleString = rrule.rruleString;
        }

        const experienceSchedule = {
            ...values,
            startUTC,
            endUTC,
            rRule: rruleString,
            timezone: values?.timezone,
        };

        if (experienceSchedule?.priceType === PRICE_TYPE.FIXED) {
            experienceSchedule.priceGroupId = null;
        }

        if (experienceSchedule?.priceType === PRICE_TYPE.PRICE_GROUP) {
            experienceSchedule.price = 0;
        }

        if (submitType === SUBMIT_TYPE.ADD) {
            const success = await addExperienceSchedule(experienceSchedule);

            if (success) {
                const successMessage = values?.repeat ? translate('add_all') : translate('add_one');
                toast.success(successMessage, {
                    position: 'top-right',
                    theme: 'colored',
                });

                getEvents(calendarRange, experienceSchedule?.experienceDetailId, experienceSchedule?.experienceId);
            }

            onClose();
        } else {
            setEventToUpdate({ ...experienceSchedule });

            if (submitType === SUBMIT_TYPE.UPDATE) {
                setOpenUpdate(true);
            }

            if (submitType === SUBMIT_TYPE.DELETE) {
                setOpenDelete(true);
            }
        }
    };

    const onUpdate = async (values) => {
        let success = false;
        if (values?.thisAndFollowing) {
            // UPDATE PREVIOUS EVENTS
            await updatePastEvents(values);

            // GENERATE NEW EVENTS FROM TODAY ONWARDS
            success = await addExperienceSchedule({
                ...values,
                oldCapacity: clickedEvent?.capacity,
                oldStartUTC: clickedEvent?.startUTC,
                oldUnlimited: clickedEvent?.unlimited,
            });
        } else {
            success = await updateExperienceSchedule({
                expId: values?.experienceId,
                schId: values?.id,
                all: values?.all || false,
                oldStartUTC: clickedEvent?.startUTC,
                oldEndUTC: clickedEvent?.endUTC,
                ...values,
            });
        }

        if (success) {
            const successMessage = (values?.repeat || clickedEvent?.repeat) && (values?.thisAndFollowing || values?.all) ? translate('update_all') : translate('update_one');
            toast.success(successMessage, {
                position: 'top-right',
                theme: 'colored',
            });

            getEvents(calendarRange, values?.experienceDetailId, values?.experienceId);
        }

        onClose();
    };

    const onDelete = async (values) => {
        let success = false;

        if (values?.thisAndFollowing) {
            // UPDATE PREVIOUS EVENTS
            success = await updatePastEvents({ ...values, isDelete: true });
        } else {
            success = await deleteExperienceSchedule({
                expId: values?.experienceId,
                schId: values?.id,
                all: values?.all,
                ...values,
            });
        }

        if (success) {
            const successMessage = (values?.repeat || clickedEvent?.repeat) && (values?.thisAndFollowing || values?.all) ? translate('remove_all') : translate('remove_one');
            toast.success(successMessage, {
                position: 'top-right',
                theme: 'colored',
            });

            getEvents(calendarRange, values?.experienceDetailId, values?.experienceId);
        }

        onClose();
    };

    const updatePastEvents = async (values) => {
        const oldRRuleString = clickedEvent?.rrule || '';
        if (oldRRuleString) {
            const startDate = new Date(values?.startUTC);
            const untilDate = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate() - 1, 23, 59, 59, 999));

            const startUTC = clickedEvent?.startUTC;
            const timezone = clickedEvent?.timezone;
            const rruleValues = rruleExtractor(oldRRuleString, startUTC, timezone);
            const dateValues = getDateValuesFromUTC(startUTC, clickedEvent?.endUTC, timezone);

            const newRRule = buildRRule({ ...dateValues, timezone, ...rruleValues, untilType: UNTIL_TYPE.DATE, untilDate });
            let rruleString = null;
            if (newRRule?.rruleString) {
                rruleString = newRRule.rruleString;
            }

            return await updateExperienceSchedule({
                expId: values?.experienceId,
                schId: values?.id,
                all: true,
                isDelete: values?.isDelete,
                ...clickedEvent,
                rRule: rruleString,
            });
        }

        return false;
    };

    const onEventClick = (clickedEvent) => {
        const event = clickedEvent?.event?.extendedProps;
        if (!event?.experienceDetailId) return;

        setIsEdit(true);
        const { id, experienceScheduleId, experienceDetailId, scheduleExceptionsId, experienceScheduleCode, startUTC, endUTC, timezone, timePeriod, capacity, unlimited, privateSlot, units, price, currency, priceGroupId, rRule } = event;

        const { repeat, selectedWeekDays, frequency, intradayRepeat, byHour, byMinute, untilType, untilCount, untilDate } = rruleExtractor(rRule, startUTC, timezone);

        const byHourDate = byHour !== null ? buildByDurationDate(byHour) : null;
        const byMinuteDate = byMinute !== null ? buildByDurationDate(0, byMinute) : null;

        const { start, startDuration, endDuration, end } = getDateValuesFromUTC(startUTC, endUTC, timezone);
        const hasEndDate = start && end && !start.hasSame(end, 'day');

        setClickedEvent({
            id,
            experienceScheduleId,
            experienceDetailId,
            scheduleExceptionsId,
            experienceScheduleCode,
            start,
            startDuration,
            endDuration,
            hasEndDate,
            end,
            startUTC,
            endUTC,
            timezone,
            timePeriod,
            capacity,
            unlimited,
            privateSlot,
            units,
            priceType: isValidPositiveInteger(priceGroupId) ? PRICE_TYPE.PRICE_GROUP : PRICE_TYPE.FIXED,
            price,
            currency,
            priceGroupId,
            repeat,
            selectedWeekDays,
            frequency,
            intradayRepeat,
            byHour,
            byHourDate,
            byMinute,
            byMinuteDate,
            untilType,
            untilCount,
            untilDate,
            rrule: rRule,
        });
        onSelect({
            jsEvent: clickedEvent?.jsEvent,
            start: clickedEvent?.event?.start,
            end: clickedEvent?.event?.end,
        });
    };

    const buildByDurationDate = (byHour = 0, byMinute = 0) => {
        const now = DateTime.local();

        const byDurationDate = now.set({
            hour: byHour,
            minute: byMinute,
            second: 0,
            millisecond: 0,
        });

        return byDurationDate;
    };

    return {
        onEventClick,
        calendar: {
            calendarRange,
            setCalendarRange,
            onSelect,
            onClose,
            getEvents,
            onSubmit,
            onUpdate,
            onDelete,
            start,
            end,
            timezone,
            setTimezone,
        },
        popOver: {
            show: open,
            position,
            clickedEvent,
            isEdit,
        },
        futureEventsModal: {
            openUpdate,
            openDelete,
            eventToUpdate,
        },
    };
};
